import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { MOCK_PROJECTS, MOCK_BRANDS } from '@/data/mockData';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson } from '@/lib/api';

const AppContext = createContext();

const INITIAL_PROJECT_ID = `proj-init-${Date.now()}`;
const REFS_STORAGE_KEY = 'ruido_project_references_v1';

// ---- Helpers ---------------------------------------------------------------

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const buildSeedProject = () => ({
  id: INITIAL_PROJECT_ID,
  name: 'New Video Project',
  client: { name: 'You', avatar: null },
  brandId: null,
  status: 'new',
  videoType: '',
  duration: '',
  style: '',
  description: '',
  createdAt: new Date().toISOString(),
  deliveryDeadline: new Date(Date.now() + 72 * 3600000).toISOString(),
  hasNewUpdate: false,
  qualityControl: null,
  delivery: null,
  internalNotes: '',
  messages: [
    {
      id: `msg-${Date.now()}`,
      type: 'system',
      content: "Welcome to your new project! Tell us about the video you'd like to create. You can describe your vision, upload references, or choose a video type below.",
      sender: 'system',
      timestamp: new Date().toISOString(),
      actions: [
        { label: 'Promotional', value: 'Promotional' },
        { label: 'Storytelling', value: 'Storytelling' },
        { label: 'Social Reel', value: 'Social Media Reel' },
        { label: 'Product Showcase', value: 'Product Showcase' },
        { label: 'Lookbook', value: 'Lookbook / Fashion' }
      ]
    }
  ]
});

// Map a Supabase row → component-shaped project
const mapDbProject = (row, ownerLabel = 'You') => ({
  id: row.id,
  name: row.name || 'New Video Project',
  client: { name: ownerLabel, avatar: null },
  brandId: null,
  status: row.status || 'new',
  videoType: row.video_type || '',
  duration: row.duration || '',
  style: row.style || '',
  description: row.description || '',
  createdAt: row.created_at,
  deliveryDeadline: row.delivery_deadline || new Date(Date.now() + 72 * 3600000).toISOString(),
  hasNewUpdate: !!row.has_new_update,
  needsAttention: !!row.needs_attention,
  revisionCount: row.revision_count || 0,
  qualityControl: null,
  delivery: null,
  internalNotes: '',
  ownerId: row.owner_id,
  messages: [],
});

// Map a Supabase message row → component-shaped message
const mapDbMessage = (row) => {
  const meta = row.metadata || {};
  // Priority: explicit message_type wins. `delivery` rows may carry
  // sender_type='system' (legacy seeds) — the delivery bubble must still render.
  let type;
  if (row.message_type === 'delivery') type = 'delivery';
  else if (row.message_type === 'brief') type = 'brief';
  else if (row.message_type === 'status_update') type = 'status_update';
  else if (row.message_type === 'system' || row.sender_type === 'system') type = 'system';
  else if (row.sender_type === 'team') type = 'team';
  else type = 'user';
  return {
    id: row.id,
    type,
    sender: row.sender_type || 'user',
    content: row.content || '',
    timestamp: row.created_at,
    ...(meta.actions ? { actions: meta.actions } : {}),
    ...(meta.files ? { files: meta.files } : {}),
    ...(meta.delivery ? { delivery: meta.delivery } : {}),
    ...(meta.newStatus ? { newStatus: meta.newStatus } : {}),
    ...(meta.attachments ? { attachments: meta.attachments } : {}),
    ...(row.message_type === 'brief' ? { briefMeta: {
      brand_name: meta.brand_name, objective: meta.objective,
      video_type: meta.video_type, duration_range: meta.duration_range,
      audience: meta.audience, style: meta.style, concept: meta.concept,
      estimated_credits: meta.estimated_credits,
    } } : {}),
  };
};

// ---- Provider --------------------------------------------------------------

export function AppProvider({ children }) {
  const { isLoggedIn, session, profile } = useAuth();
  const isManager = profile?.role === 'manager' || profile?.role === 'admin';

  const [projects, setProjects] = useState(() => {
    // No mocks at boot for any user. We hydrate from Supabase when logged in
    // and only switch to mock fallback when explicitly logged out (dev preview).
    return [];
  });
  const [brands] = useState(MOCK_BRANDS);
  const [activeClientProjectId, setActiveClientProjectId] = useState(null);
  const [activeGestorProjectId, setActiveGestorProjectId] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const lastUserId = useRef(null);

  const authHeaders = useCallback(() => {
    const token = session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [session]);

  const apiFetch = useCallback(async (path, options = {}) => {
    const method = (options.method || 'GET').toUpperCase();
    const body = options.body ? JSON.parse(options.body) : null;
    const { ok, status, data } = await xhrJson(method, `${API}${path}`, body, {
      ...authHeaders(),
      ...(options.headers || {}),
    });
    if (!ok) {
      const detail = (data && data.detail) || `Request failed: ${path} (${status})`;
      throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
    return data || {};
  }, [authHeaders]);

  // --- Hydration from Supabase when logged in ---
  const hydrateFromSupabase = useCallback(async () => {
    try {
      const { projects: rows } = await apiFetch('/api/projects');
      const ownerLabel = profile?.full_name || 'You';
      // Bulk-load messages and files in parallel
      const detailed = await Promise.all((rows || []).map(async (row) => {
        const base = mapDbProject(row, isManager ? 'Client' : ownerLabel);
        try {
          const [{ messages }, { files }] = await Promise.all([
            apiFetch(`/api/projects/${row.id}/messages`),
            apiFetch(`/api/projects/${row.id}/files`),
          ]);
          base.messages = (messages || []).map(mapDbMessage);
          base.files = files || [];
        } catch (e) {
          base.messages = [];
          base.files = [];
        }
        return base;
      }));

      // Note: we do NOT auto-seed an empty project anymore.
      // The user sees the empty state and can press "New Project" to create one.

      setProjects(detailed);
      // Set active project: client → newest; manager → first
      if (detailed.length) {
        if (!isManager) setActiveClientProjectId(detailed[0].id);
        if (isManager) setActiveGestorProjectId(detailed[0].id);
      } else {
        setActiveClientProjectId(null);
        setActiveGestorProjectId(null);
      }
      setHydrated(true);
    } catch (e) {
      // When logged in, never fall back to mock data — stay empty
      console.warn('Hydration failed:', e);
      setProjects([]);
      setActiveClientProjectId(null);
      setActiveGestorProjectId(null);
      setHydrated(true);
    }
  }, [apiFetch, profile, isManager]);

  useEffect(() => {
    const userId = session?.user?.id || null;
    if (isLoggedIn && userId && lastUserId.current !== userId) {
      lastUserId.current = userId;
      setHydrated(false);
      hydrateFromSupabase();
    } else if (!isLoggedIn) {
      // Logged out → mock preview (dev/anon mode only)
      if (lastUserId.current !== null) {
        lastUserId.current = null;
      }
      // Only seed mock once, when projects is empty
      setProjects(prev => {
        if (prev.length > 0) return prev;
        return [buildSeedProject(), ...MOCK_PROJECTS];
      });
      setActiveClientProjectId(prev => prev || INITIAL_PROJECT_ID);
      setActiveGestorProjectId(prev => prev || (MOCK_PROJECTS[0]?.id || null));
      setHydrated(true);
    }
  }, [isLoggedIn, session, hydrateFromSupabase]);

  // --- Selectors ---
  const getProject = useCallback((id) => projects.find(p => p.id === id), [projects]);
  const getBrand = useCallback((id) => brands.find(b => b.id === id), [brands]);

  // --- Mutators (sync API, async Supabase under the hood) ---

  const createProject = useCallback((name) => {
    const id = isLoggedIn ? newId() : `proj-${Date.now()}`;
    const baseProject = isLoggedIn
      ? { ...buildSeedProject(), id, name, client: { name: profile?.full_name || 'You', avatar: null } }
      : { ...buildSeedProject(), id, name };
    setProjects(prev => [baseProject, ...prev]);

    if (isLoggedIn) {
      apiFetch('/api/projects', {
        method: 'POST',
        body: JSON.stringify({ id, name }),
      }).catch(e => console.warn('createProject sync failed:', e));
    }
    return id;
  }, [isLoggedIn, profile, apiFetch]);

  const addMessage = useCallback((projectId, message) => {
    const id = isLoggedIn ? newId() : `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const enriched = { ...message, id, timestamp: new Date().toISOString() };

    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, messages: [...p.messages, enriched] } : p
    ));

    if (isLoggedIn) {
      const { actions, files, delivery, newStatus, type, sender, content, ...rest } = message;
      const senderType =
        type === 'system' ? 'system'
        : type === 'team' ? 'team'
        : type === 'delivery' ? 'delivery'
        : 'user';
      const messageType =
        type === 'system' ? 'system'
        : type === 'status_update' ? 'status_update'
        : type === 'delivery' ? 'delivery'
        : 'text';
      const metadata = {};
      if (actions) metadata.actions = actions;
      if (files) metadata.files = files;
      if (delivery) metadata.delivery = delivery;
      if (newStatus) metadata.newStatus = newStatus;

      apiFetch(`/api/projects/${projectId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          id,
          content: content || '',
          sender_type: senderType,
          message_type: messageType,
          metadata,
        }),
      }).catch(e => console.warn('addMessage sync failed:', e));
    }
  }, [isLoggedIn, apiFetch]);

  const updateProject = useCallback((projectId, updates) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...updates } : p));

    if (isLoggedIn) {
      const dbUpdates = {};
      if ('name' in updates) dbUpdates.name = updates.name;
      if ('status' in updates) dbUpdates.status = updates.status;
      if ('videoType' in updates) dbUpdates.video_type = updates.videoType;
      if ('duration' in updates) dbUpdates.duration = updates.duration;
      if ('style' in updates) dbUpdates.style = updates.style;
      if ('description' in updates) dbUpdates.description = updates.description;
      if ('deliveryDeadline' in updates) dbUpdates.delivery_deadline = updates.deliveryDeadline;
      if ('hasNewUpdate' in updates) dbUpdates.has_new_update = updates.hasNewUpdate;
      if ('needsAttention' in updates) dbUpdates.needs_attention = updates.needsAttention;
      if (Object.keys(dbUpdates).length === 0) return;

      apiFetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(dbUpdates),
      }).catch(e => console.warn('updateProject sync failed:', e));
    }
  }, [isLoggedIn, apiFetch]);

  const updateProjectStatus = useCallback((projectId, newStatus) => {
    const statusMessages = {
      received: 'Your project has been received.',
      in_production: 'Your project is now in production.',
      ready: 'Your video is ready for review!',
      delivered: 'Your video has been delivered. Thank you!',
      revision_requested: "You've requested a revision. Our team will review your feedback.",
    };

    // Optimistic local update
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      const statusMessage = {
        id: `msg-status-${Date.now()}`,
        type: 'status_update',
        content: statusMessages[newStatus] || 'Project status updated.',
        sender: 'system',
        timestamp: new Date().toISOString(),
        newStatus,
      };
      const updates = { status: newStatus, messages: [...p.messages, statusMessage] };
      if (newStatus === 'ready' || newStatus === 'delivered') updates.hasNewUpdate = true;
      if (newStatus === 'revision_requested') {
        updates.deliveryDeadline = new Date(Date.now() + 24 * 3600000).toISOString();
        updates.revisionCount = (p.revisionCount || 0) + 1;
        updates.hasNewUpdate = false;
      }
      return { ...p, ...updates };
    }));

    if (isLoggedIn) {
      const dbUpdate = { status: newStatus };
      if (newStatus === 'ready' || newStatus === 'delivered') dbUpdate.has_new_update = true;
      if (newStatus === 'revision_requested') {
        dbUpdate.delivery_deadline = new Date(Date.now() + 24 * 3600000).toISOString();
        dbUpdate.has_new_update = false;
      }
      apiFetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(dbUpdate),
      }).catch(e => console.warn('updateProjectStatus sync failed:', e));

      // Persist the status_update message
      apiFetch(`/api/projects/${projectId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content: statusMessages[newStatus] || 'Project status updated.',
          sender_type: 'system',
          message_type: 'status_update',
          metadata: { newStatus },
        }),
      }).catch(e => console.warn('status message sync failed:', e));
    }
  }, [isLoggedIn, apiFetch]);

  const markProjectRead = useCallback((projectId) => {
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, hasNewUpdate: false } : p
    ));
    if (isLoggedIn) {
      apiFetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify({ has_new_update: false }),
      }).catch(() => {});
    }
  }, [isLoggedIn, apiFetch]);

  // --- File uploads (logged-in) ---
  const uploadProjectFile = useCallback(async (projectId, file) => {
    if (!isLoggedIn) return null;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('bucket', 'project-files');
    const res = await fetch(`${API}/api/storage/upload/${projectId}`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body: fd,
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  }, [isLoggedIn, authHeaders]);

  // --- Production references (per project, persisted locally) ---
  const [productionRef, setProductionRef] = useState(null);

  const [projectReferences, setProjectReferences] = useState(() => {
    try {
      const raw = localStorage.getItem(REFS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(REFS_STORAGE_KEY, JSON.stringify(projectReferences));
    } catch {
      // storage may be full
    }
  }, [projectReferences]);

  const addProjectReferences = useCallback((projectId, items) => {
    if (!projectId || !items?.length) return;
    setProjectReferences(prev => ({
      ...prev,
      [projectId]: [...(prev[projectId] || []), ...items],
    }));
  }, []);

  const removeProjectReference = useCallback((projectId, refId) => {
    setProjectReferences(prev => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter(r => r.id !== refId),
    }));
  }, []);

  return (
    <AppContext.Provider value={{
      projects, brands,
      activeClientProjectId, setActiveClientProjectId,
      activeGestorProjectId, setActiveGestorProjectId,
      getProject, getBrand,
      createProject, addMessage, updateProjectStatus, updateProject, markProjectRead,
      uploadProjectFile,
      productionRef, setProductionRef,
      projectReferences, addProjectReferences, removeProjectReference,
      // Auth-derived flags
      isLoggedIn, isManager, hydrated,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
